/**
 * One voice call's state, outside React (RFC 0307 S1).
 *
 * Fetches the secret, connects the provider, and keeps what the composer
 * draws: status, mute states, level, the last error. What the call is FOR,
 * its instructions, tools and where its transcript goes, is asked for at
 * start, so the session wiring (S2 to S4) plugs in without this knowing it.
 */

import type { VoicePlan } from '@mod/core/voice'
import type {
  ComposerVoice,
  VoiceCall,
  VoiceProvider,
  VoiceSecret,
  VoiceStatus,
} from './types'

/** The core plan (`@mod/core/voice`), plus how this window watches the
 *  session once the call is live. */
export interface VoiceCallPlan extends VoicePlan {
  /** The call is live: the place to start watching the session (S4). */
  onLive?(call: VoiceCall): () => void
}

export interface VoiceControllerDeps {
  provider: VoiceProvider
  fetchSecret(): Promise<VoiceSecret>
  /** Asked once per call, when it starts. */
  plan(): VoiceCallPlan
}

type State = Omit<
  ComposerVoice,
  'start' | 'stop' | 'toggleMic' | 'toggleSpeaker'
>

/** How often the mic level may re-render the composer: about 10Hz. */
const LEVEL_INTERVAL_MS = 100

export class VoiceController {
  private state: State = {
    status: 'idle',
    micMuted: false,
    speakerMuted: false,
    level: 0,
  }
  private call: VoiceCall | null = null
  private cleanups: Array<() => void> = []
  private attempt = 0
  /** Cancels the attempt still connecting, so a hang-up reaches it. */
  private connecting: AbortController | null = null
  private listeners = new Set<() => void>()

  constructor(private deps: VoiceControllerDeps) {}

  /** The composer's view, rebuilt only when something changed. */
  snapshot(): ComposerVoice {
    return this.view
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  readonly start = (): void => {
    if (this.state.status === 'connecting' || this.state.status === 'live')
      return
    const attempt = ++this.attempt
    this.set({ status: 'connecting', error: undefined, level: 0 })
    void this.open(attempt)
  }

  readonly stop = (): void => {
    this.attempt++
    this.connecting?.abort()
    this.connecting = null
    this.close()
    this.set({ status: 'idle', level: 0, micMuted: false })
  }

  readonly toggleMic = (): void => {
    const micMuted = !this.state.micMuted
    this.call?.setMicMuted(micMuted)
    this.set({ micMuted, ...(micMuted ? { level: 0 } : {}) })
  }

  readonly toggleSpeaker = (): void => {
    const speakerMuted = !this.state.speakerMuted
    this.call?.setSpeakerMuted(speakerMuted)
    this.set({ speakerMuted })
  }

  private async open(attempt: number): Promise<void> {
    const cancel = new AbortController()
    this.connecting = cancel
    try {
      const secret = await this.deps.fetchSecret()
      if (attempt !== this.attempt) return
      const plan = this.deps.plan()
      // Compared in onEnd rather than the `call` const below, which is not
      // assigned until connect resolves.
      let current: VoiceCall | null = null
      const call = await this.deps.provider.connect({
        clientSecret: secret.clientSecret,
        ...(secret.exchangeSdp ? { exchangeSdp: secret.exchangeSdp } : {}),
        instructions: plan.instructions,
        tools: plan.tools,
        ...(plan.respondToSpeech === false ? { respondToSpeech: false } : {}),
        onToolCall: plan.onToolCall,
        onTranscript: plan.onTranscript,
        ...(plan.onSpeechEnded ? { onSpeechEnded: plan.onSpeechEnded } : {}),
        signal: cancel.signal,
        onEnd: (reason) => {
          if (!current || this.call !== current) return
          this.call = null
          this.close()
          this.set(
            reason?.error
              ? { status: 'error', error: reason.error, level: 0 }
              : { status: 'idle', level: 0 },
          )
        },
      })
      if (attempt !== this.attempt) {
        call.hangUp()
        return
      }
      if (this.connecting === cancel) this.connecting = null
      current = call
      this.call = call
      call.setSpeakerMuted(this.state.speakerMuted)
      let lastLevel = 0
      let lastAt = 0
      this.cleanups.push(
        call.onLevel((level) => {
          // The level comes every animation frame and every change re-renders
          // whoever holds this controller, a whole thread. Bars read fine at
          // 10Hz, and a change under 4% is not worth a render at all.
          const now = Date.now()
          if (now - lastAt < LEVEL_INTERVAL_MS && level !== 0) return
          if (Math.abs(level - lastLevel) < 0.04) return
          lastLevel = level
          lastAt = now
          this.set({ level })
        }),
      )
      const stopWatching = plan.onLive?.(call)
      if (stopWatching) this.cleanups.push(stopWatching)
      this.set({ status: 'live' })
    } catch (err) {
      if (attempt !== this.attempt) return
      this.close()
      this.set({
        status: 'error',
        error: err instanceof Error ? err.message : String(err),
        level: 0,
      })
    }
  }

  private close(): void {
    for (const c of this.cleanups.splice(0)) c()
    const call = this.call
    this.call = null
    call?.hangUp()
  }

  private view: ComposerVoice = this.build()

  private build(): ComposerVoice {
    return {
      ...this.state,
      start: this.start,
      stop: this.stop,
      toggleMic: this.toggleMic,
      toggleSpeaker: this.toggleSpeaker,
    }
  }

  private set(patch: Partial<State> & { status?: VoiceStatus }): void {
    this.state = { ...this.state, ...patch }
    this.view = this.build()
    for (const l of this.listeners) l()
  }
}
